import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Play, Pause, Activity, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { ErrorAlert, getErrorMessage } from '@nimbitlabs/ui';
import { PageHeader } from '@nimbitlabs/layouts';
import { CreateMonitoringConfigModal } from '@/components/CreateMonitoringConfigModal';
import { EditMonitoringConfigModal } from '@/components/EditMonitoringConfigModal';
import { PermissionGate } from '@nimbitlabs/features';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, useToast, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@nimbitlabs/ui';
import type { ServiceMonitoringConfigurationDto } from '@/types/api';

interface Environment {
  id: string;
  name: string;
}

export const ServiceMonitoring = () => {
  const [configs, setConfigs] = useState<ServiceMonitoringConfigurationDto[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<ServiceMonitoringConfigurationDto | null>(null);
  const { toast } = useToast();
  
  // Error states
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configsData, environmentsData] = await Promise.all([
        api.serviceMonitoringConfigurations.getAll(),
        api.environments.getAll(),
      ]);
      
      setConfigs(configsData);
      
      // Sort environments by tier (dev -> uat -> prod)
      const sortedEnvs = [...environmentsData].sort((a, b) => {
        const tierOrder = ['development', 'dev', 'staging', 'uat', 'qa', 'test', 'production', 'prod'];
        const getTier = (name: string) => {
          const lowerName = name?.toLowerCase() || '';
          const index = tierOrder.findIndex(tier => lowerName.includes(tier));
          return index === -1 ? 999 : index;
        };
        return getTier(a.name) - getTier(b.name);
      });
      
      setEnvironments(sortedEnvs);
      
      if (sortedEnvs.length > 0 && !selectedEnvironmentId) {
        setSelectedEnvironmentId(sortedEnvs[0].id);
      }
    } catch (err) {
      console.error('Failed to load monitoring data:', err);
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!selectedConfig) return;
    setActionError(null);

    try{
      await api.serviceMonitoringConfigurations.delete(selectedConfig.id);
      toast({
        title: 'Success',
        description: 'Monitoring deleted successfully',
      });
      loadData();
    } catch (error) {
      console.error('Failed to delete monitoring:', error);
      setActionError(getErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedConfig(null);
    }
  };

  const handleToggleEnabled = async (config: ServiceMonitoringConfigurationDto) => {
    try {
      if (config.isEnabled) {
        await api.serviceMonitoringConfigurations.disable(config.id);
      } else {
        await api.serviceMonitoringConfigurations.enable(config.id);
      }
      toast({
        title: 'Success',
        description: `Monitoring ${config.isEnabled ? 'disabled' : 'enabled'} successfully`,
      });
      loadData();
    } catch (error) {
      console.error('Failed to toggle monitoring:', error);
      toast({
        title: 'Error',
        description: 'Failed to update monitoring',
        variant: 'destructive',
      });
      setActionError(getErrorMessage(error));
    }
  };

  const getStatusBadge = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || 'unknown';
    switch (normalizedStatus) {
      case 'healthy':
        return <Badge className="bg-green-500/20 text-green-600 border-green-500/40 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40"><CheckCircle2 className="w-3 h-3 mr-1" />Healthy</Badge>;
      case 'unhealthy':
      case 'timeout':
      case 'dnsfailure':
        return <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400"><AlertCircle className="w-3 h-3 mr-1" />Unhealthy</Badge>;
      case 'degraded':
        return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/40 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/40"><AlertCircle className="w-3 h-3 mr-1" />Degraded</Badge>;
      default:
        return <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  const formatInterval = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h`;
  };

  const formatLastCheck = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'An unknown error occurred';
  };

  const filteredConfigs = configs.filter(c => c.environmentId === selectedEnvironmentId);

  if (loading) {
    return (
      <div className="space-y-6 p-4 md:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Skeleton className="h-9 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-40" />
        </div>
        <Skeleton className="h-12 w-full" />
        <div className="grid gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Service Monitoring"
        description="Configure automated health checks for your services"
        actions={
          <PermissionGate permission="environments:create">
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Monitoring
            </Button>
          </PermissionGate>
        }
      />

      {/* Error Display */}
      <ErrorAlert error={loadError || actionError} />

      {environments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No environments found</h3>
            <p className="text-muted-foreground text-center">
              Create an environment first to set up monitoring
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={selectedEnvironmentId} onValueChange={setSelectedEnvironmentId} className="space-y-6">
          <div className="w-full overflow-x-auto py-3 px-1 sm:px-0">
            <div className="inline-flex w-max min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-[4px]">
              <TabsList className="inline-flex h-12 items-center justify-start gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1.5 backdrop-blur-sm w-full">
                {environments.map((env) => (
                  <TabsTrigger 
                    key={env.id} 
                    value={env.id}
                    className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                  >
                    <span className="relative z-10">{env.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

            {environments.map((env) => (
              <TabsContent key={env.id} value={env.id} className="mt-6">
                {filteredConfigs.length === 0 ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Activity className="w-12 h-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No monitoring in {env.name}</h3>
                      <p className="text-muted-foreground text-center">
                        Click "Add Monitoring" to create health checks for this environment
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-6">
                    {filteredConfigs.map((config) => (
                      <Card key={config.id} className={`relative overflow-hidden transition-all duration-300 ${!config.isEnabled ? 'opacity-60' : ''}`}>
                        {/* Status Indicator Bar */}
                        <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-lg ${
                          config.currentHealthStatus?.toLowerCase() === 'healthy' ? 'bg-green-500' :
                          config.currentHealthStatus?.toLowerCase() === 'unhealthy' ? 'bg-red-500' :
                          config.currentHealthStatus?.toLowerCase() === 'degraded' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`} />

                        <CardHeader className="pb-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div className="flex-1 space-y-3">
                              {/* Service Name & Status */}
                              <div className="flex flex-wrap items-center gap-2">
                                <CardTitle className="text-2xl font-bold">{config.serviceName}</CardTitle>
                                {!config.isEnabled && (
                                  <Badge variant="secondary" className="text-xs">Paused</Badge>
                                )}
                              </div>

                              {/* Health Status */}
                              <div className="flex items-center gap-3">
                                {getStatusBadge(config.currentHealthStatus)}
                                <span className="text-sm text-muted-foreground">•</span>
                                <span className="text-sm text-muted-foreground">
                                  Last check: {formatLastCheck(config.lastHealthCheckAt)}
                                </span>
                              </div>

                              {/* Endpoint */}
                              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-md border border-muted w-fit">
                                <Badge variant="outline" className="text-xs font-mono">{config.method}</Badge>
                                <span className="font-mono text-sm">{config.url}</span>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 sm:flex-col sm:items-end">
                              <PermissionGate permission="environments:update">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleEnabled(config)}
                                  className="gap-2"
                                >
                                  {config.isEnabled ? (
                                    <>
                                      <Pause className="w-3.5 h-3.5" />
                                      <span className="hidden sm:inline">Pause</span>
                                    </>
                                  ) : (
                                    <>
                                      <Play className="w-3.5 h-3.5" />
                                      <span className="hidden sm:inline">Resume</span>
                                    </>
                                  )}
                                </Button>
                              </PermissionGate>
                              <PermissionGate permission="environments:update">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedConfig(config);
                                    setEditModalOpen(true);
                                  }}
                                  className="gap-2"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                  <span className="hidden sm:inline">Edit</span>
                                </Button>
                              </PermissionGate>
                              <PermissionGate permission="environments:delete">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedConfig(config);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="gap-2"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span className="hidden sm:inline">Delete</span>
                                </Button>
                              </PermissionGate>
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="pt-0">
                          {/* Health Metrics */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                            <div className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border border-blue-500/20 p-5 transition-all hover:shadow-lg hover:shadow-blue-500/10 hover:border-blue-500/30">
                              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full blur-2xl group-hover:bg-blue-500/10 transition-colors" />
                              <div className="relative">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                    <Clock className="w-4 h-4" />
                                  </div>
                                  <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Check Interval</span>
                                </div>
                                <div className="text-2xl font-bold bg-gradient-to-br from-blue-600 to-blue-500 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
                                  {formatInterval(config.intervalMinutes * 60)}
                                </div>
                              </div>
                            </div>

                            <div className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500/10 via-purple-500/5 to-transparent border border-purple-500/20 p-5 transition-all hover:shadow-lg hover:shadow-purple-500/10 hover:border-purple-500/30">
                              <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/5 rounded-full blur-2xl group-hover:bg-purple-500/10 transition-colors" />
                              <div className="relative">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400">
                                    <Activity className="w-4 h-4" />
                                  </div>
                                  <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Timeout</span>
                                </div>
                                <div className="text-2xl font-bold bg-gradient-to-br from-purple-600 to-purple-500 dark:from-purple-400 dark:to-purple-300 bg-clip-text text-transparent">
                                  {config.timeoutSeconds}s
                                </div>
                              </div>
                            </div>

                            <div className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500/10 via-green-500/5 to-transparent border border-green-500/20 p-5 transition-all hover:shadow-lg hover:shadow-green-500/10 hover:border-green-500/30">
                              <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/5 rounded-full blur-2xl group-hover:bg-green-500/10 transition-colors" />
                              <div className="relative">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="p-2 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400">
                                    <CheckCircle2 className="w-4 h-4" />
                                  </div>
                                  <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Expected Status</span>
                                </div>
                                <div className="text-2xl font-bold bg-gradient-to-br from-green-600 to-green-500 dark:from-green-400 dark:to-green-300 bg-clip-text text-transparent">
                                  {config.expectedStatusCode}
                                </div>
                              </div>
                            </div>

                            <div className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent border border-orange-500/20 p-5 transition-all hover:shadow-lg hover:shadow-orange-500/10 hover:border-orange-500/30">
                              <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/5 rounded-full blur-2xl group-hover:bg-orange-500/10 transition-colors" />
                              <div className="relative">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                    <AlertCircle className="w-4 h-4" />
                                  </div>
                                  <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Failure Threshold</span>
                                </div>
                                <div className="text-2xl font-bold bg-gradient-to-br from-orange-600 to-orange-500 dark:from-orange-400 dark:to-orange-300 bg-clip-text text-transparent">
                                  {config.failureThreshold}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Additional Config */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-muted/20 rounded-lg border border-muted">
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Retry Count</div>
                              <div className="text-sm font-medium">{config.retryCount}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Retry Delay</div>
                              <div className="text-sm font-medium">{config.retryDelaySeconds}s</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Alerts</div>
                              <div className="text-sm font-medium">{config.enableAlerts ? 'Enabled' : 'Disabled'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Consecutive Failures</div>
                              <div className={`text-sm font-bold ${config.consecutiveHealthFailures > 0 ? 'text-red-500' : ''}`}>
                                {config.consecutiveHealthFailures}
                              </div>
                            </div>
                          </div>

                          {/* Authentication Info */}
                          {config.authType !== 'None' && (
                            <div className="mt-4 p-3 bg-muted/20 rounded-lg border border-muted">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground">Authentication:</span>
                                <span className="font-medium">{config.authType}</span>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
        </Tabs>
      )}

      <CreateMonitoringConfigModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          loadData();
        }}
      />

      {selectedConfig && (
        <EditMonitoringConfigModal
          open={editModalOpen}
          config={selectedConfig}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedConfig(null);
          }}
          onSuccess={() => {
            setEditModalOpen(false);
            setSelectedConfig(null);
            loadData();
          }}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Monitoring</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this monitoring for{' '}
              <strong>{selectedConfig?.serviceName}</strong> in{' '}
              <strong>{selectedConfig?.environmentName}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
